import { useFormDirtyStore } from '@/stores/form-dirty-store';
import { Form as AntForm, type FormInstance, type FormProps } from 'antd';
import {
  type JSX,
  type ReactNode,
  type Ref,
  forwardRef,
  useEffect,
} from 'react';
import { LeavePageAlert } from './LeavePageAlert';

type LeaveAlertCondition =
  | {
      additionalAlertCondition: boolean;
      isDirty?: never;
      isSubmitting?: never;
      isSubmitSuccessful?: never;
    }
  | {
      additionalAlertCondition?: never;
      isDirty: boolean;
      isSubmitting: boolean;
      /** Submit 成功後に即遷移する場合など */
      isSubmitSuccessful: boolean;
    };

type BaseFormProps<Values = any> = {
  leavePageAlertCondition: LeaveAlertCondition | null;
  children?: ReactNode;
} & Omit<FormProps<Values>, 'children'>;

/* ---------- ref-forwarding 本体 ---------- */
function InnerForm<Values = any>(
  { leavePageAlertCondition, children, ...restProps }: BaseFormProps<Values>,
  ref: Ref<FormInstance<Values>>,
) {
  const {
    isDirty,
    isSubmitting,
    isSubmitSuccessful,
    additionalAlertCondition,
  } = leavePageAlertCondition ?? {};

  const { setIsDirty } = useFormDirtyStore();

  useEffect(() => {
    if (isDirty !== undefined) {
      setIsDirty(
        !!(
          (isDirty && !isSubmitting && !isSubmitSuccessful) ||
          additionalAlertCondition
        ),
      );
    }
  }, [
    isDirty,
    isSubmitting,
    isSubmitSuccessful,
    setIsDirty,
    additionalAlertCondition,
  ]);

  return (
    <>
      <AntForm ref={ref} {...restProps}>
        {children}
      </AntForm>
      {leavePageAlertCondition && <LeavePageAlert />}
    </>
  );
}

/**
 * forwardRef() はジェネリックを失うので、あとから as キャストで
 * "ジェネリック付き関数コンポーネント" 型を付与し直す
 */
const BaseForm = forwardRef(InnerForm) as <Values = any>(
  props: BaseFormProps<Values> & {
    ref?: Ref<FormInstance<Values>>;
  },
) => JSX.Element;

/* ---------- Ant の静的メンバを合成 ---------- */
export const Form = Object.assign(BaseForm, {
  Item: AntForm.Item,
  List: AntForm.List,
  ErrorList: AntForm.ErrorList,
  useForm: AntForm.useForm,
  useWatch: AntForm.useWatch,
});
